import React from "react"

import { EntityDetail, EntityDetailColumn, EntityDetailRow } from "@/components/EntityDetail"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"
import { Person } from "@/types/person"
import { formatPersonShortName } from "@/utils/personDisplay"

import AccountsTab from "./tabs/AccountsTab"
import DiaryEntriesTab from "./tabs/DiaryEntriesTab"
import DocumentsTab from "./tabs/DocumentsTab"
import FinanceDetailsTab from "./tabs/FinanceDetailsTab"
import PersonTypeDetailsTab from "./tabs/PersonTypeDetailsTab"
import PersonalDetailsTab from "./tabs/PersonalDetailsTab"
import SheriffTab from "./tabs/SheriffTab"
import UniformTab from "./tabs/UniformTab"

interface PersonDetailsProps {
  person: Person
  primaryPerson?: Person | null
  onSelectPerson: (person: Person) => void
  onClose: () => void
}

const PersonDetails = ({ person, primaryPerson, onSelectPerson, onClose }: PersonDetailsProps) => {
  const { claims } = useAuth()

  const teamPermissions = claims.team_permissions

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800"
      case "Inactive":
        return "bg-palette-jungleGreen100 text-palette-jungleGreen700"
      case "Banned":
        return "bg-red-100 text-red-800"
      default:
        return "bg-palette-jungleGreen100 text-palette-jungleGreen700"
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ]

    const day = date.getDate()
    const suffix = (day: number) => {
      if (day > 3 && day < 21) return "th"
      switch (day % 10) {
        case 1:
          return "st"
        case 2:
          return "nd"
        case 3:
          return "rd"
        default:
          return "th"
      }
    }

    return `${months[date.getMonth()]} ${day}${suffix(day)}, ${date.getFullYear()}`
  }

  const calculateServiceLength = (startDate: string | null) => {
    if (!startDate) return "N/A"
    const start = new Date(startDate)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 30) {
      return `${diffDays} days`
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return `${months} month${months > 1 ? "s" : ""}`
    } else {
      const years = Math.floor(diffDays / 365)
      const remainingMonths = Math.floor((diffDays % 365) / 30)
      if (remainingMonths === 0) {
        return `${years} year${years > 1 ? "s" : ""}`
      }
      return `${years} year${years > 1 ? "s" : ""}, ${remainingMonths} month${
        remainingMonths > 1 ? "s" : ""
      }`
    }
  }

  return (
    <EntityDetail
      icon="user"
      title={`${person.first_name} ${person.surname}`}
      titleAdornments={
        <Badge className={`shrink-0 pointer-events-none ${getStatusColor(person.status)}`}>
          {person.status || "Unknown"}
        </Badge>
      }
      summary={
        <>
          {/* Column 1: Identity & Contact */}
          <EntityDetailColumn>
            <EntityDetailRow icon="hash">{person.person_id || "N/A"}</EntityDetailRow>
            <EntityDetailRow icon="envelope">
              <span className="break-all">{person.email || "N/A"}</span>
            </EntityDetailRow>
            <EntityDetailRow icon="phone">{person.phone_number || "N/A"}</EntityDetailRow>
          </EntityDetailColumn>

          {/* Column 2: Contract Information */}
          <EntityDetailColumn>
            <EntityDetailRow icon="calendarBlank" label="Contract start">
              {formatDate(person.contract_start_date)}
            </EntityDetailRow>
            <EntityDetailRow icon="calendarBlank" label="Service length">
              {calculateServiceLength(person.contract_start_date)}
            </EntityDetailRow>
            {primaryPerson && (
              <EntityDetailRow
                icon="user"
                label="Primary"
                onClick={() => onSelectPerson(primaryPerson)}
              >
                <span className="font-semibold">{formatPersonShortName(primaryPerson)}</span>
              </EntityDetailRow>
            )}
          </EntityDetailColumn>

          {/* Column 3: Location & Role */}
          <EntityDetailColumn>
            <EntityDetailRow icon="mapPin">
              {person.location?.length ? person.location.join(", ") : "N/A"}
            </EntityDetailRow>
            <EntityDetailRow icon="truck">{person.role || "N/A"}</EntityDetailRow>
            <EntityDetailRow icon="company">{person.sheriff_employer || "N/A"}</EntityDetailRow>
          </EntityDetailColumn>
        </>
      }
      onClose={onClose}
    >
      <Tabs defaultValue="personal" className="h-full">
        <TabsList className="flex w-full h-auto justify-start overflow-x-auto overflow-y-hidden bg-palette-jungleGreen000">
          {teamPermissions?.includes("view.tab.personal") && (
            <TabsTrigger className="shrink-0" value="personal">
              Personal
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.person_type") && (
            <TabsTrigger className="shrink-0" value="driver">
              Person Type
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.documents") && (
            <TabsTrigger className="shrink-0" value="documents">
              Documents
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.finance") && (
            <TabsTrigger className="shrink-0" value="finance">
              Finance
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.accounts") && (
            <TabsTrigger className="shrink-0" value="accounts">
              Accounts
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.uniform") && (
            <TabsTrigger className="shrink-0" value="uniform">
              Uniform
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.diary") && (
            <TabsTrigger className="shrink-0" value="diary">
              Diary
            </TabsTrigger>
          )}
          {teamPermissions?.includes("view.tab.sheriff") && (
            <TabsTrigger className="shrink-0" value="sheriff">
              Sheriff
            </TabsTrigger>
          )}
        </TabsList>

        <div className="mt-6 h-full">
          {teamPermissions?.includes("view.tab.personal") && (
            <TabsContent value="personal" className="h-full">
              <PersonalDetailsTab person={person} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.person_type") && (
            <TabsContent value="driver" className="h-full">
              <PersonTypeDetailsTab person={person} onSelectPerson={onSelectPerson} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.documents") && (
            <TabsContent value="documents" className="h-full">
              <DocumentsTab person={person} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.finance") && (
            <TabsContent value="finance" className="h-full">
              <FinanceDetailsTab person={person} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.accounts") && (
            <TabsContent value="accounts" className="h-full">
              <AccountsTab person={person} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.uniform") && (
            <TabsContent value="uniform" className="h-full">
              <UniformTab person={person} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.diary") && (
            <TabsContent value="diary" className="h-full">
              <DiaryEntriesTab person={person} />
            </TabsContent>
          )}
          {teamPermissions?.includes("view.tab.sheriff") && (
            <TabsContent value="sheriff" className="h-full">
              <SheriffTab person={person} />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </EntityDetail>
  )
}

export default PersonDetails
